import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';
import {
  baseApiUrl,
} from '@/env';
import {
  toast,
} from '@/hooks/useToast';
// (auth storage handled elsewhere)

// @INFO: !!! uncomment if you need to mock API

// import MockAdapter from 'axios-mock-adapter';
// mock samples below

export const axiosInstance = axios.create({ baseURL: baseApiUrl });
const axiosExternalInstance = axios.create();
const mockedUrls = new Set<string>([]);

// Global logout function reference
let globalLogout: (() => void) | null = null;

// Refresh token logic removed – Auth0 SPA SDK handles silent refresh client-side

// Function to set the global logout function
export const setGlobalLogout = (logoutFn: () => void) => {
  globalLogout = logoutFn;
};

// Function to reset refresh attempts (call after successful login)
export const resetRefreshAttempts = () => {};

// Function to trigger logout manually (for testing)
export const triggerGlobalLogout = () => {
  console.log('HTTP Client: Manual logout trigger');
  if (globalLogout) {
    globalLogout();
  } else {
    console.warn('HTTP Client: Global logout function not set');
  }
};

// (No refresh token flow here)

// Function to set auth headers
export const setAuthHeaders = (token?: string, tenantOrg?: string, patientHash?: string) => {
  if (token) {
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
};

// Global error handler for Axios errors
export const handleGlobalApiError = (error: AxiosError) => {
  const status = error.response?.status;
  const errorData = error.response?.data as any;
  const plainError = typeof errorData === 'string' ? errorData : null;
  const errorMessage = plainError || errorData?.message || error.message || 'An unexpected error occurred';

  // Handle 401 errors for logout
  if (status === 401) {
    console.log('Global Error Handler: 401 Unauthorized detected');
    // Note: Logout should be handled by the HTTP client or auth context
    return; // Don't show toast for 401 as it will redirect
  }

  // Show toast for other axios errors
  switch (status) {
  case 400:
    toast.error(errorMessage);
    break;
  case 403:
    toast.error('Access denied. You do not have permission to perform this action.');
    break;
  case 404:
    toast.error('Resource not found. Please check your request.');
    break;
  case 422:
    toast.error(`Validation Error: ${errorMessage}`);
    break;
  case 429:
    toast.error(errorMessage ?? 'Too many requests. Please try again later.');
    break;
  case 500:
    toast.error('Server error. Please try again later.');
    break;
  case 502:
  case 503:
  case 504:
    toast.error('Service temporarily unavailable. Please try again later.');
    break;
  default:
    if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
      toast.error('Network error. Please check your connection and try again.');
    } else if (status) {
      toast.error(errorMessage);
    }
    break;
  }
};

// Handle 401 errors (no refresh retry)
const handle401Error = async (error: AxiosError): Promise<AxiosError> => {
  const status = error.response?.status;
  if (status === 401) {
    if (globalLogout) {
      globalLogout();
    }
  }

  return Promise.reject(error);
};

// Response interceptor for handling errors
const setupResponseInterceptors = () => {
  // Interceptor for main axios instance
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Check if this is our test endpoint for successful responses
      if (response.config?.url === '/api/test-success') {
        // Simulate successful response
        const mockResponse: AxiosResponse = {
          data: { message: 'Success' },
          status: 200,
          statusText: 'OK',
          headers: {},
          config: response.config as any,
        };
        return mockResponse;
      }
      return response;
    },
    async (error: AxiosError) => {
      // Suppress toast for 400 errors from CheckEligibility endpoint; UI handles display
      const urlPath = (error.config?.url || '').toLowerCase();
      if (urlPath.includes('/api/ch/checkeligibility') && error.response?.status === 400) {
        return Promise.reject(error);
      }

      // Check if this is our test endpoint for 401 errors
      if (error.config?.url === '/api/test-401') {
        // Simulate 401 error
        const mockError = new Error('401 Unauthorized') as AxiosError;
        mockError.response = {
          status: 401,
          statusText: 'Unauthorized',
          data: { message: 'Token expired or invalid' },
          headers: {},
          config: error.config as any,
        };
        return Promise.reject(mockError);
      }

      // Check if this is our test endpoint for 400 errors
      if (error.config?.url === '/api/test-400') {
        // Simulate 400 error
        const mockError = new Error('400 Bad Request') as AxiosError;
        mockError.response = {
          status: 400,
          statusText: 'Bad Request',
          data: { message: 'This is a test 400 error for toast testing' },
          headers: {},
          config: error.config as any,
        };
        return Promise.reject(mockError);
      }

      // Handle 401 errors with token refresh
      try {
        const result = await handle401Error(error);
        return result;
      } catch (refreshError) {
        // If refresh failed, show toast and reject
        handleGlobalApiError(error);
        return Promise.reject(error);
      }
    },
  );

  // Interceptor for external axios instance
  axiosExternalInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      // Handle 401 errors with token refresh
      try {
        const result = await handle401Error(error);
        return result;
      } catch (refreshError) {
        // If refresh failed, show toast and reject
        handleGlobalApiError(error);
        return Promise.reject(error);
      }
    },
  );
};

// Setup interceptors immediately
setupResponseInterceptors();

type RequestFlags = {
  isMock?: boolean;
  isExternal?: boolean;
};

export function sendRequest<T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
  flags?: RequestFlags,
): Promise<T> {
  const { isMock = false, isExternal = false } = flags ?? {};
  const httpClient = isMock
    || isExternal
    || mockedUrls.has(config.url?.toLowerCase() ?? '')
    ? axiosExternalInstance
    : axiosInstance;
  const source = axios.CancelToken.source();
  const promise = httpClient({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }: AxiosResponse<T>) => data);
  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled.');
  };
  return promise;
}

// @INFO: !!! uncomment if you need to mock API
// const mockHttpAdapter = new MockAdapter(axiosMockInstance);
// e.g
// mockHttpAdapter
//   .onPost('/api/EFax/GetFaxes')
//   .reply(200, faxList);
